import Loader from '@/components/Loader';
import ServiceSelector from '@/components/service-selector/ServiceSelector';
import { calculateCogsInEUR, groupCogsByCurrency } from '@/lib/cogs';
import { formatPrice } from '@/lib/helpers';
import { TRatesResponse, useRates } from '@/models/rates';
import {
  TChildService,
  TService,
  useAddService,
  useChildServices,
  useExcludeService,
  useIncludeService,
  useIncludedAgentServices,
  useReorderChildServices,
} from '@/models/services';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Drawer,
  Flex,
  Popover,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PiDotsSix, PiMinusCircleFill } from 'react-icons/pi';
import classes from './IncludedServices.module.css';
import ServiceEditor from './ServiceEditor';

function Cogs({
  rates,
  serviceId,
}: {
  cogsByCurrency: Record<string, number>;
  rates: TRatesResponse;
  serviceId: number;
}) {
  const [opened, { open, close }] = useDisclosure(false);
  const { data: agentServices = [] } = useIncludedAgentServices(serviceId);

  const agentServicesCogs = useMemo(
    () => groupCogsByCurrency(agentServices),
    [agentServices],
  );
  const cogsInEUR = calculateCogsInEUR(agentServicesCogs, rates);

  if (Object.keys(agentServicesCogs).length === 0) {
    return (
      <Center>
        <Text fz={'xs'}>-</Text>
      </Center>
    );
  }

  return (
    <Popover opened={opened} onClose={close} withArrow position="left">
      <Popover.Target>
        <Text fz={'xs'} onMouseEnter={open} onMouseLeave={close}>
          {formatPrice(cogsInEUR, 'EUR')}
        </Text>
      </Popover.Target>
      <Popover.Dropdown>
        <Table fz={'xs'} fw={300}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th fw={300}>Service</Table.Th>
              <Table.Th fw={300}>
                <Center>COGS</Center>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {agentServices.map((service) => (
              <Table.Tr key={service.id}>
                <Table.Td>{service.name}</Table.Td>
                <Table.Td ta={'right'}>
                  {formatPrice(service.cogs, service.currency)}
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td fw={500}>Total (EUR)</Table.Td>
              <Table.Td ta={'right'} fw={500}>
                {formatPrice(cogsInEUR, 'EUR')}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Popover.Dropdown>
    </Popover>
  );
}

function SortableServices({
  children,
  rates,
  excludeService,
  parentId,
  parentCurrency,
  onServiceClick,
}: {
  children: TChildService[];
  rates: TRatesResponse;
  excludeService: (serviceId: number, serviceName: string) => void;
  parentId: number;
  parentCurrency: string;
  onServiceClick?: (service: TChildService) => void;
}) {
  const isReordering = useRef(false);
  const [services, setServices] = useState(children);
  const { mutate: reorderServices } = useReorderChildServices(parentId);

  useEffect(() => {
    if (!isReordering.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs server-provided children into local reorderable list
      setServices(children);
    }
  }, [children]);

  const handleDragEnd = ({ destination, source }: any) => {
    if (!destination || destination.index === source.index) return;
    isReordering.current = true;
    const newOrder = [...services];
    const [removed] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, removed);
    setServices(newOrder);
    const reorderedServices = newOrder.map((service) => ({ id: service.id }));
    reorderServices(reorderedServices, {
      onSettled: () => {
        isReordering.current = false;
      },
    });
  };

  const draggableServices = services.map((s, index) => (
    <Draggable key={s.id} index={index} draggableId={String(s.id)}>
      {(provided) => (
        <Table.Tr ref={provided.innerRef} {...provided.draggableProps}>
          <Table.Td p={0}>
            <ActionIcon variant="transparent" {...provided.dragHandleProps}>
              <PiDotsSix />
            </ActionIcon>
          </Table.Td>
          <Table.Td w={'100%'} p={0}>
            <Text
              className={classes.textTruncate}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => onServiceClick?.(s)}
              size="xs"
            >
              {s.name}
            </Text>
          </Table.Td>
          <Table.Td ta={'right'}>{formatPrice(s.price, s.currency)}</Table.Td>
          <Table.Td ta={'right'}>
            <Cogs
              cogsByCurrency={s.cogsByCurrency}
              rates={rates}
              serviceId={s.id}
            />
          </Table.Td>
          <Table.Td p={0}>
            <ActionIcon
              variant="transparent"
              onClick={() => excludeService(s.id, s.name)}
            >
              <PiMinusCircleFill size={18} />
            </ActionIcon>
          </Table.Td>
        </Table.Tr>
      )}
    </Draggable>
  ));

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Table fz={'xs'} fw={300} highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={0}></Table.Th>
            <Table.Th fw={300}>Service</Table.Th>
            <Table.Th fw={300}>
              <Center>Price</Center>
            </Table.Th>
            <Table.Th fw={300}>
              <Center>COGS</Center>
            </Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Droppable droppableId="services">
          {(provided) => (
            <Table.Tbody ref={provided.innerRef} {...provided.droppableProps}>
              {draggableServices}
              {provided.placeholder}
            </Table.Tbody>
          )}
        </Droppable>
        <Table.Tfoot>
          <Table.Tr
            style={{
              borderTop:
                '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
              borderBottom: 'none',
            }}
          >
            <Table.Td></Table.Td>
            <Table.Td></Table.Td>
            <Table.Td ta={'right'} fw={500}>
              {formatPrice(
                services.map((s) => s.price).reduce((a, b) => a + b, 0),
                parentCurrency,
              )}
            </Table.Td>
            <Table.Td ta={'right'} fw={500}>
              {formatPrice(
                services
                  .map((s) => calculateCogsInEUR(s.cogsByCurrency, rates))
                  .reduce((a, b) => a + b, 0),
                'EUR',
              )}
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>
    </DragDropContext>
  );
}

function CreateServiceButton({
  parentService,
  onUpdated,
}: {
  parentService: TService;
  onUpdated?: (service: TService) => void;
}) {
  const [opened, { open, close }] = useDisclosure(false);
  const { mutate: addService } = useAddService(parentService.id);
  return (
    <>
      <Drawer opened={opened} onClose={close} position="right" size="400px">
        <ServiceEditor
          title={`Create included service in "${parentService.name}"`}
          titleOrder={4}
          hideStandaloneFlag={true}
          initialJurisidctions={parentService.jurisdictions}
          initialCurrency={parentService.currency}
          onSave={(service) => {
            addService(service as Omit<TService, 'id'>, {
              onSuccess: () => {
                notifications.show({
                  title: 'Done',
                  message: `Service "${service.name}" added successfully`,
                  color: 'green',
                  position: 'top-right',
                });
                onUpdated?.(service as TService);
                close();
              },
            });
          }}
        />
      </Drawer>
      <Button size="xs" variant="light" onClick={open}>
        New Service
      </Button>
    </>
  );
}

function IncludeServiceButton({ parentService }: { parentService: TService }) {
  const [opened, { open, close }] = useDisclosure(false);
  const { mutate: includeService } = useIncludeService(parentService.id);

  return (
    <>
      <ServiceSelector
        opened={opened}
        onClose={close}
        currency={parentService.currency}
        parentServiceId={parentService.id}
        jurisdiction={parentService.jurisdictions[0]}
        onSelect={(service) => {
          includeService(service.id, {
            onSuccess: () => {
              notifications.show({
                title: 'Done',
                color: 'green',
                message: `Service "${service.name}" included in "${parentService.name}" successfully`,
                position: 'top-right',
              });
              close();
            },
          });
        }}
      />
      <Button size="xs" variant="light" onClick={open}>
        Include existing service
      </Button>
    </>
  );
}

export default function IncludedServices({
  parentService,
  onChanged,
  onServiceClick,
}: {
  parentService: TService;
  onChanged: (sum: number) => void;
  onServiceClick?: (service: TChildService) => void;
}) {
  const { data: rates } = useRates();
  const { data: children = [], isLoading } = useChildServices(parentService.id);
  const { mutate: excludeService } = useExcludeService(parentService.id);

  useEffect(() => {
    if (isLoading) return;
    onChanged(children.reduce((sum, c) => sum + c.price, 0));
  }, [onChanged, children, isLoading]);

  if (!children || !rates) {
    return <Loader />;
  }

  return (
    <>
      <Text m="sm" fz={'sm'} fw={500}>
        Included services
      </Text>
      <Box p="sm">
        {!parentService || !parentService.id ? (
          <Center>
            <Text size="xs">
              In order to add a containing service, you need to save this
              service first.
            </Text>
          </Center>
        ) : (
          <>
            {children.length > 0 ? (
              <SortableServices
                children={children}
                rates={rates}
                excludeService={(id, name) =>
                  excludeService(id, {
                    onSuccess: () => {
                      notifications.show({
                        title: 'Done',
                        message: `Service "${name}" excluded from "${parentService.name}" successfully`,
                        color: 'green',
                        position: 'top-right',
                      });
                    },
                  })
                }
                parentCurrency={parentService.currency}
                parentId={parentService.id}
                onServiceClick={onServiceClick}
              />
            ) : (
              <Center>
                <Text size="xs">No services included</Text>
              </Center>
            )}
            <Flex gap={'sm'} mt={'sm'} justify="flex-end">
              <CreateServiceButton parentService={parentService} />
              <IncludeServiceButton parentService={parentService} />
            </Flex>
          </>
        )}
      </Box>
    </>
  );
}
